import { useGetAmendmentByIdQueryV2 } from '@xemplo/amendment-query';
import { Button, ButtonType, ButtonVariant } from '@xemplo/button';
import { PAYRUN_COMPONENTS } from '@xemplo/gp-constants';
import { DawnTrash, DawnTriangleAlert } from '@xemplo/icons';
import { Prompt, PromptDialogSlot } from '@xemplo/prompt-dialog';
import { RoleRenderer } from '@xemplo/role-renderer';
import { AuthorisationModule } from '@xemplo/role-types';
import { useRoleAuthorisation } from '@xemplo/roles-query';
import { Colour } from '@xemplo/style-constants';

import * as S from './remove-amendment-button.styles';
import { amendmentCanBeRemoved } from './remove-amendment-helper';
import { useRemoveAmendment } from './use-remove-amendment';

export interface RemoveAmendmentButtonProps {
  onSuccess?: () => void;
  amendmentId: string;
}

export const RemoveAmendmentButtonTestId = {
  RemoveAmendmentButton: 'remove-amendment-button',
  RemoveAmendmentPrompt: 'remove-amendment-prompt',
};

export const RemoveAmendmentButton = (props: RemoveAmendmentButtonProps) => {
  const { onSuccess, amendmentId } = props;
  const { data } = useGetAmendmentByIdQueryV2({ id: amendmentId });
  const { componentDisabled } = useRoleAuthorisation(AuthorisationModule.payrun);
  const { promptDialog, handleConfirm, handleCancel } = useRemoveAmendment({
    amendmentId,
    onSuccess: () => onSuccess?.(),
  });

  if (!data) return null;

  return (
    <RoleRenderer
      module={AuthorisationModule.payrun}
      component={PAYRUN_COMPONENTS.AMENDMENT_DELETE}
      additionalRules={[() => amendmentCanBeRemoved(data)]}
    >
      <div>
        <Button
          testId={RemoveAmendmentButtonTestId.RemoveAmendmentButton}
          ariaLabel="delete amendment button"
          type={ButtonType.Tertiary}
          icon={<DawnTrash fontSize={16} />}
          variant={ButtonVariant.Error}
          onClick={promptDialog.openModalDialog}
          disabled={componentDisabled(PAYRUN_COMPONENTS.AMENDMENT_DELETE)}
        >
          Remove
        </Button>
        <Prompt.Dialog {...promptDialog.dialogProps}>
          {promptDialog.isOpen && (
            <Prompt>
              <Prompt.Slot
                name={PromptDialogSlot.Header}
                render={
                  <Prompt.Header
                    title="Remove Amendment"
                    icon={<DawnTriangleAlert color={Colour.Red[500]} />}
                  />
                }
              />
              <Prompt.Slot
                name={PromptDialogSlot.Body}
                render={
                  <Prompt.Body>
                    <S.PromptBody>
                      Are you sure you want to remove amendment? You can't undo changes.
                    </S.PromptBody>
                  </Prompt.Body>
                }
              />
              <Prompt.Slot
                name={PromptDialogSlot.Footer}
                render={
                  <Prompt.Footer
                    btnPrimary={{
                      label: 'Remove',
                      ariaLabel: 'confirm remove button',
                      onClickAsync: handleConfirm,
                      type: ButtonType.Destructive,
                    }}
                    btnTertiary={{
                      label: 'Cancel',
                      ariaLabel: 'cancel remove button',
                      type: ButtonType.Tertiary,
                      onClick: handleCancel,
                    }}
                  />
                }
              />
            </Prompt>
          )}
        </Prompt.Dialog>
      </div>
    </RoleRenderer>
  );
};
